import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGetHangmanWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const TIMER_START = 60;

function shuffle(str) {
  const arr = str.split('');
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const result = arr.join('');
  return result === str ? shuffle(str) : result;
}

export default function WordScramble() {
  const [word, setWord]           = useState('');
  const [scrambled, setScrambled] = useState('');
  const [guess, setGuess]         = useState('');
  const [timeLeft, setTimeLeft]   = useState(TIMER_START);
  const [score, setScore]         = useState(0);
  const [streak, setStreak]       = useState(0);
  const [status, setStatus]       = useState('idle'); // idle | playing | correct | wrong | timeout
  const [loading, setLoading]     = useState(false);
  const [shake, setShake]         = useState(false);
  const [showShare, setShowShare] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  const fetchWord = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiGetHangmanWord();
      const w = data.word.toUpperCase();
      setWord(w);
      setScrambled(shuffle(w));
      setGuess('');
      setStatus('playing');
      setTimeLeft(TIMER_START);
    } catch { setStatus('idle'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (status === 'playing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setStatus('timeout');
            setStreak(0);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [status]);

  useEffect(() => {
    if (status === 'timeout') {
      const t = setTimeout(() => setShowShare(true), 1000);
      return () => clearTimeout(t);
    }
  }, [status]);

  useEffect(() => {
    if (status === 'playing') inputRef.current?.focus();
  }, [status, scrambled]);

  function handleGuess(e) {
    e.preventDefault();
    if (status !== 'playing') return;
    if (guess.toUpperCase() === word) {
      clearInterval(timerRef.current);
      const bonus = Math.ceil(timeLeft / 10);
      const points = word.length + bonus + streak;
      setScore(s => s + points);
      setStreak(s => s + 1);
      setStatus('correct');
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setGuess('');
    }
  }

  function reshuffle() {
    setScrambled(shuffle(word));
  }

  const timerPct = (timeLeft / TIMER_START) * 100;
  const timerColor = timeLeft > 20 ? '#16a34a' : timeLeft > 10 ? '#d97706' : '#dc2626';

  const shareText = `⏱️ WordZingo Word Scramble\nFinal Score: ${score} pts 🏆\n🎯 Can you beat ${score} points? wordzingo.com/word-scramble`;

  return (
    <div className="game-page">
      <PageSEO page="word-scramble" />
      {showShare && (
        <ShareModal
          shareText={shareText}
          gameUrl="https://wordzingo.com/word-scramble"
          onClose={() => setShowShare(false)}
          onNewGame={() => { setScore(0); setStreak(0); setShowShare(false); fetchWord(); }}
        />
      )}
      <div className="container" style={{maxWidth:600}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">⏱️</span>
          <h1>Word Scramble</h1>
          <p>Unscramble the word before time runs out — faster guesses earn bonus points!</p>
        </div>

        {/* Score bar */}
        <div className="solver-card mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-center">
              <div style={{fontSize:'2rem', fontWeight:900, color:'var(--primary)', fontFamily:'Fredoka One,cursive'}}>{score}</div>
              <div className="text-muted small fw-bold">SCORE</div>
            </div>
            <div className="text-center">
              <div style={{fontSize:'2rem', fontWeight:900, color:'#d97706', fontFamily:'Fredoka One,cursive'}}>{streak}</div>
              <div className="text-muted small fw-bold">STREAK 🔥</div>
            </div>
            <div className="text-center">
              <div style={{fontSize:'2rem', fontWeight:900, color:timerColor, fontFamily:'Fredoka One,cursive'}}>{timeLeft}s</div>
              <div className="text-muted small fw-bold">TIME</div>
            </div>
          </div>
          {status === 'playing' && (
            <div className="mt-2" style={{height:6, background:'#e5e7eb', borderRadius:99}}>
              <div style={{height:'100%', width:`${timerPct}%`, background:timerColor, borderRadius:99, transition:'width 1s linear'}} />
            </div>
          )}
        </div>

        {status === 'idle' && (
          <div className="solver-card text-center">
            <div style={{fontSize:'4rem'}}>⏱️</div>
            <h3 className="fw-bold">Ready to Play?</h3>
            <p className="text-muted">A scrambled word appears — unscramble it before 60 seconds run out!</p>
            <button className="solve-btn" onClick={fetchWord} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm"></span> : '🚀 Start Game'}
            </button>
          </div>
        )}

        {(status === 'playing' || status === 'correct') && word && (
          <div className="solver-card text-center">
            <div className="fw-bold text-muted small mb-3">UNSCRAMBLE THIS WORD</div>
            <div className={`d-flex justify-content-center gap-2 mb-4 flex-wrap ${shake ? 'wordle-shake' : ''}`}>
              {scrambled.split('').map((ch, i) => (
                <div key={i} style={{
                  width:52, height:56, borderRadius:10,
                  background: status === 'correct' ? '#d1fae5' : 'var(--primary)',
                  color:'#fff', fontWeight:900, fontSize:'1.6rem',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'Fredoka One, cursive',
                  boxShadow:'0 3px 8px rgba(0,0,0,0.15)',
                }}>{ch}</div>
              ))}
            </div>

            {status === 'correct' ? (
              <div className="mb-3">
                <div style={{fontSize:'2rem'}}>🎉</div>
                <div className="fw-bold text-success fs-4">Correct! It was <strong>{word}</strong></div>
                <div className="text-muted small">+{word.length + Math.ceil(timeLeft/10) + (streak-1)} points</div>
              </div>
            ) : (
              <form onSubmit={handleGuess}>
                <input
                  ref={inputRef}
                  className="big-input text-center"
                  style={{letterSpacing:4, textTransform:'uppercase', maxWidth:300, margin:'0 auto'}}
                  value={guess}
                  onChange={e => setGuess(e.target.value.replace(/[^a-zA-Z]/g, ''))}
                  placeholder="Your answer…"
                  maxLength={word.length}
                  autoComplete="off"
                />
              </form>
            )}

            <div className="d-flex gap-2 justify-content-center mt-3">
              {status === 'correct' ? (
                <button className="solve-btn" onClick={fetchWord} disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm"></span> : '➡️ Next Word'}
                </button>
              ) : (
                <>
                  <button className="btn fw-bold" onClick={reshuffle}
                    style={{borderRadius:50, background:'var(--border)', border:'none', fontFamily:'Nunito,sans-serif'}}>
                    🔄 Reshuffle
                  </button>
                  <button className="btn fw-bold" onClick={() => { setStatus('timeout'); clearInterval(timerRef.current); setStreak(0); }}
                    style={{borderRadius:50, background:'var(--border)', border:'none', fontFamily:'Nunito,sans-serif', color:'#dc2626'}}>
                    ⏩ Skip
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {status === 'timeout' && (
          <div className="solver-card text-center">
            <div style={{fontSize:'3rem'}}>⏰</div>
            <h3 className="fw-bold">Time's Up!</h3>
            <p className="text-muted">The word was: <strong className="text-dark">{word}</strong></p>
            <p>Final score: <strong style={{fontSize:'1.5rem', color:'var(--primary)'}}>{score}</strong></p>
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <button className="solve-btn" onClick={() => { setScore(0); setStreak(0); setShowShare(false); fetchWord(); }}>
                🔄 Play Again
              </button>
              <button className="btn fw-bold"
                style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
                onClick={() => setShowShare(true)}>
                📤 Share Score
              </button>
            </div>
          </div>
        )}
        <SeoContent game="word-scramble" />
      </div>
    </div>
  );
}
